import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  collection, getDocs, doc, updateDoc,
  setDoc, deleteDoc, serverTimestamp, writeBatch, query, where,
  getCountFromServer,
} from "firebase/firestore";
import Modal from "../../components/Modal";
import UISwitch from "../../components/UISwitch";
import { useLanguage } from "../../context/LanguageContext";
import {
  HardHat, Phone, Mail, Lock,
  Pencil, Trash2, Plus, AlertTriangle, CheckCircle,
  Search, Eye, EyeOff,
} from "lucide-react";


function OperatorsManager({ companyId }) {
  const { t } = useLanguage();

  const [operators,  setOperators]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [success,    setSuccess]    = useState("");
  const [saving,     setSaving]     = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [showPw,     setShowPw]     = useState(false);

  // ── Modal state ────────────────────────────────────────────────────────────
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editingOp,   setEditingOp]   = useState(null); // null = create mode

  // ── Delete state ───────────────────────────────────────────────────────────
  const [deleteModal,     setDeleteModal]     = useState(false);
  const [deleteTarget,    setDeleteTarget]    = useState(null);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);

  // ── Selection state ────────────────────────────────────────────────────────
  const [selectMode, setSelectMode] = useState(false);
  const [selected,   setSelected]   = useState(new Set());

  // ── Search ─────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");

  // ── Form state ─────────────────────────────────────────────────────────────
  const emptyForm = {
    firstName: "", lastName: "", email: "",
    phone: "", password: "", notes: "", active: true,
  };
  const [form, setForm] = useState(emptyForm);

  // ── Client count per operator ──────────────────────────────────────────────
  const [clientCounts, setClientCounts] = useState({});

  // ── Load operators ─────────────────────────────────────────────────────────
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const snap = await getDocs(
          query(collection(db, "companies", companyId, "users"), where("role", "==", "operator"))
        );
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setOperators(list);

        // Count clients per operator in parallel using aggregate queries (no document downloads)
        const countResults = await Promise.all(
          list.map(op =>
            getCountFromServer(
              query(collection(db, "companies", companyId, "clients"), where("created_by", "==", op.id))
            ).then(s => [op.id, s.data().count])
          )
        );
        setClientCounts(Object.fromEntries(countResults));
      } catch (err) {
        setError("Error loading operators: " + err.message);
      }
      setLoading(false);
    }
    load();
  }, [companyId]);

  // ── Flash feedback ─────────────────────────────────────────────────────────
  function flash(msg, isError = false) {
    if (isError) { setError(msg); setTimeout(() => setError(""), 4000); }
    else         { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); }
  }

  // ── Open modals ────────────────────────────────────────────────────────────
  function openCreate() {
    setEditingOp(null);
    setForm(emptyForm);
    setShowPw(false);
    setError("");
    setModalOpen(true);
  }

  function openEdit(op) {
    setEditingOp(op);
    setForm({
      firstName: op.first_name || "",
      lastName:  op.last_name  || "",
      email:     op.email      || "",
      phone:     op.phone      || "",
      password:  "",
      notes:     op.notes      || "",
      active:    op.active !== false,
    });
    setShowPw(false);
    setError("");
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingOp(null);
    setForm(emptyForm);
    setError("");
  }

  // ── Save operator ──────────────────────────────────────────────────────────
  async function handleSave(e) {
    e.preventDefault();
    setError("");
    if (!form.firstName.trim()) return setError("First name is required.");
    if (!form.email.trim())     return setError("Email is required.");
    if (!editingOp && form.password.length < 6)
      return setError("Password must be at least 6 characters.");

    setSaving(true);
    try {
      if (editingOp) {
        // ── Edit existing operator ──
        const data = {
          first_name: form.firstName.trim(),
          last_name:  form.lastName.trim(),
          name:       `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
          email:      form.email.trim(),
          phone:      form.phone.trim(),
          notes:      form.notes.trim(),
          active:     form.active,
          updated_at: serverTimestamp(),
        };
        // Only update password if provided
        if (form.password.trim()) data.password = form.password.trim();

        await updateDoc(doc(db, "companies", companyId, "users", editingOp.id), data);
        setOperators(prev => prev.map(o =>
          o.id === editingOp.id ? { ...o, ...data, password: undefined } : o
        ));
        flash("✅ Operator updated!");

      } else {
        // ── Create new operator via Firestore trigger ──
        const opId = "operator-" + Date.now().toString(36);
        const data = {
          first_name: form.firstName.trim(),
          last_name:  form.lastName.trim(),
          name:       `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
          email:      form.email.trim(),
          password:   form.password.trim(), // Cloud Function reads and deletes this
          phone:      form.phone.trim(),
          role:       "operator",
          notes:      form.notes.trim(),
          active:     form.active,
          companyId:  companyId,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        };

        await setDoc(doc(db, "companies", companyId, "users", opId), data);
        setOperators(prev => [...prev, { id: opId, ...data, password: undefined }]);
        flash(`✅ Operator "${form.firstName}" created! Login account being set up...`);
      }

      closeModal();
    } catch (err) {
      setError("Error: " + err.message);
    }
    setSaving(false);
  }

  // ── Toggle active ──────────────────────────────────────────────────────────
  async function toggleActive(opId, newActive) {
    // Optimistic update — flip UI immediately so it feels responsive
    setOperators(prev => prev.map(o => o.id === opId ? { ...o, active: newActive } : o));
    try {
      await updateDoc(doc(db, "companies", companyId, "users", opId), {
        active: newActive, updated_at: serverTimestamp(),
      });
    } catch (err) {
      // Revert on failure
      setOperators(prev => prev.map(o => o.id === opId ? { ...o, active: !newActive } : o));
      flash("Error: " + err.message, true);
    }
  }

  // ── Single delete ──────────────────────────────────────────────────────────
  function askDelete(op) { setDeleteTarget(op); setDeleteModal(true); }

  async function confirmSingleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "companies", companyId, "users", deleteTarget.id));
      setOperators(prev => prev.filter(o => o.id !== deleteTarget.id));
      setSelected(prev => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
      setDeleteModal(false);
      setDeleteTarget(null);
      flash("✅ Operator deleted.");
    } catch (err) { flash("Error: " + err.message, true); }
    setDeleting(false);
  }

  // ── Bulk delete ────────────────────────────────────────────────────────────
  async function confirmBulkDelete() {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const ids    = Array.from(selected);
      const chunks = [];
      for (let i = 0; i < ids.length; i += 500) chunks.push(ids.slice(i, i + 500));
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach(id => batch.delete(doc(db, "companies", companyId, "users", id)));
        await batch.commit();
      }
      setOperators(prev => prev.filter(o => !selected.has(o.id)));
      flash(`✅ Deleted ${selected.size} operator(s).`);
      setSelected(new Set());
      setSelectMode(false);
      setBulkDeleteModal(false);
    } catch (err) { flash("Error: " + err.message, true); }
    setDeleting(false);
  }

  // ── Selection helpers ──────────────────────────────────────────────────────
  function toggleSelect(id) {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function toggleSelectAll() {
    setSelected(
      selected.size === filtered.length
        ? new Set()
        : new Set(filtered.map(o => o.id))
    );
  }

  function exitSelectMode() { setSelectMode(false); setSelected(new Set()); }

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filtered = operators.filter(o =>
    `${o.first_name} ${o.last_name} ${o.email}`
      .toLowerCase().includes(search.toLowerCase())
  );

  const allSelected = filtered.length > 0 && filtered.every(o => selected.has(o.id));

  if (loading) return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <p className="text-gray-500 text-sm">{t("loading")}</p>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-5 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{t("op_title")}</h2>
          <p className="text-gray-500 text-sm mt-1">
            {operators.length} {t("role_operator").toLowerCase()}{operators.length !== 1 ? "s" : ""}
            {operators.filter(o => o.active !== false).length > 0 && (
              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                {operators.filter(o => o.active !== false).length} {t("op_active")}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!selectMode ? (
            <button onClick={() => setSelectMode(true)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">
              ☑️ {t("select")}
            </button>
          ) : (
            <button onClick={exitSelectMode}
              className="bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">
              ✕ {t("cancel")}
            </button>
          )}
          <button onClick={openCreate}
            className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg flex items-center gap-1.5 transition">
            <Plus className="w-4 h-4" />
            {t("op_add")}
          </button>
        </div>
      </div>

      {/* ── Info box ────────────────────────────────────────────────────────── */}
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex gap-3">
        <HardHat className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
        <p className="text-orange-700 text-sm">{t("op_access_hint")}</p>
      </div>

      {/* ── Search ──────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t("op_search_ph")}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>

      {/* ── Bulk action bar ──────────────────────────────────────────────────── */}
      {selectMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
              className="w-4 h-4 accent-green-600 cursor-pointer" />
            <span className="text-sm text-blue-800 font-medium">
              {selected.size === 0
                ? t("op_select_for_bulk")
                : `${selected.size} ${t("op_selected_count")}`}
            </span>
          </div>
          {selected.size > 0 && (
            <button onClick={() => setBulkDeleteModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg flex items-center gap-1.5">
              <Trash2 className="w-4 h-4" />
              {t("delete")} ({selected.size})
            </button>
          )}
        </div>
      )}

      {/* ── Feedback ────────────────────────────────────────────────────────── */}
      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{success}</div>}

      {/* ── Empty state ──────────────────────────────────────────────────────── */}
      {operators.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <HardHat className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">{t("op_no_operators")}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">{t("op_no_match")}</p>
        </div>
      ) : (

        /* ── Operator list ─────────────────────────────────────────────────── */
        <div className="space-y-2">
          {filtered.map(op => (
            <div key={op.id}
              className={`bg-white rounded-xl shadow-sm p-4 flex items-center gap-3 transition ${
                selected.has(op.id) ? "ring-2 ring-green-400" : ""
              }`}>

              {/* Checkbox */}
              {selectMode && (
                <input type="checkbox" checked={selected.has(op.id)}
                  onChange={() => toggleSelect(op.id)}
                  className="w-4 h-4 accent-green-600 cursor-pointer shrink-0" />
              )}

              {/* Avatar */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-semibold text-sm ${
                op.active !== false ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-400"
              }`}>
                {(op.first_name?.[0] || "?").toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-gray-800 text-sm">
                    {op.first_name} {op.last_name}
                  </p>
                  {op.active === false && (
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                      {t("disabled")}
                    </span>
                  )}
                  <span className="text-xs bg-orange-50 text-orange-600 border border-orange-100 px-2 py-0.5 rounded-full">
                    {clientCounts[op.id] || 0} {t("op_clients_count")}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Mail className="w-3 h-3" />{op.email}
                  </span>
                  {op.phone && (
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Phone className="w-3 h-3" />{op.phone}
                    </span>
                  )}
                </div>
              </div>

              {/* Toggle active */}
              <UISwitch
                value={op.active !== false}
                onChange={val => toggleActive(op.id, val)}
              />

              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(op)}
                  className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => askDelete(op)}
                  className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create / Edit Modal ──────────────────────────────────────────────── */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingOp ? t("op_edit") : t("op_new")}
      >
        <div className="space-y-4">

          {/* Access hint */}
          {!editingOp && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-orange-700 text-xs">{t("op_access_hint")}</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />{error}
            </div>
          )}

          {/* Name row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t("form_first_name")} <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={form.firstName}
                onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder={t("form_first_name")}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t("form_last_name")}
              </label>
              <input
                type="text"
                value={form.lastName}
                onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder={t("form_last_name")}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t("form_email")} <span className="text-red-400">*</span>
              <span className="ml-1 text-gray-400 font-normal">{t("form_email_login")}</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                className="w-full pl-9 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="operator@email.com"
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t("form_phone")}
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="tel"
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                className="w-full pl-9 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="+212 6xx xxx xxx"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {editingOp ? t("op_new_password") : t("form_password")}
              {editingOp && <span className="ml-1 text-gray-400 font-normal">{t("form_password_keep")}</span>}
              {!editingOp && <span className="text-red-400"> *</span>}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                className="w-full pl-9 pr-10 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder={editingOp ? t("op_new_password_ph") : t("form_password_min")}
              />
              <button type="button" onClick={() => setShowPw(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t("op_notes")}
            </label>
            <textarea
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              placeholder={t("op_notes_ph")}
            />
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between py-2 border-t border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-700">{t("form_status")}</p>
              <p className="text-xs text-gray-400">{form.active ? t("active") : t("inactive")}</p>
            </div>
            <UISwitch
              value={form.active}
              onChange={() => setForm(p => ({ ...p, active: !p.active }))}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-1">
            <button onClick={closeModal}
              className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-2.5 rounded-lg hover:bg-gray-50 transition">
              {t("cancel")}
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-medium py-2.5 rounded-lg transition flex items-center justify-center gap-2">
              {saving
                ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />{t("saving")}</>
                : <><CheckCircle className="w-4 h-4" />{editingOp ? t("save_changes") : t("op_create")}</>
              }
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Single Delete Modal ──────────────────────────────────────────────── */}
      <Modal
        isOpen={deleteModal}
        onClose={() => { setDeleteModal(false); setDeleteTarget(null); }}
        title={t("op_delete_title")}
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-700">
                {deleteTarget?.first_name} {deleteTarget?.last_name}
              </p>
              <p className="text-xs text-red-600 mt-1">{t("op_delete_msg")}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => { setDeleteModal(false); setDeleteTarget(null); }}
              className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-2.5 rounded-lg hover:bg-gray-50 transition">
              {t("cancel")}
            </button>
            <button onClick={confirmSingleDelete} disabled={deleting}
              className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-medium py-2.5 rounded-lg transition flex items-center justify-center gap-2">
              {deleting
                ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />{t("deleting")}</>
                : <><Trash2 className="w-4 h-4" />{t("yes_delete")}</>
              }
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Bulk Delete Modal ────────────────────────────────────────────────── */}
      <Modal
        isOpen={bulkDeleteModal}
        onClose={() => setBulkDeleteModal(false)}
        title={t("op_bulk_delete")}
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">
              You are about to delete <strong>{selected.size} operator{selected.size > 1 ? "s" : ""}</strong>.
              {" "}{t("delete_cannot_undo")}
            </p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setBulkDeleteModal(false)}
              className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-2.5 rounded-lg hover:bg-gray-50 transition">
              {t("cancel")}
            </button>
            <button onClick={confirmBulkDelete} disabled={deleting}
              className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-medium py-2.5 rounded-lg transition flex items-center justify-center gap-2">
              {deleting
                ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />{t("deleting")}</>
                : <><Trash2 className="w-4 h-4" />{t("delete")} {selected.size}</>
              }
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}

export default OperatorsManager;
